import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  FileText, 
  FilePlus,
  Search,
  Clock,
  MoreVertical,
  Pencil,
  Trash2,
  FileType,
  ClipboardList,
  BookOpen,
  ScrollText,
  Users,
  Folder,
  Building2,
  GitBranch,
  Signpost,
  LayoutTemplate
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOC_TYPES = [
  { id: "all", label: "All", icon: FileText },
  { id: "memo", label: "Memos", icon: FileText },
  { id: "training", label: "Training", icon: BookOpen },
  { id: "quick_reference", label: "Quick Ref", icon: FileType },
  { id: "checklist", label: "Checklists", icon: ClipboardList },
  { id: "policy", label: "Policies", icon: ScrollText },
  { id: "patient_education", label: "Patient Ed", icon: Users },
  { id: "workflow", label: "Workflows", icon: GitBranch },
  { id: "signage", label: "Signage", icon: Signpost },
  { id: "a3", label: "A3 Reports", icon: LayoutTemplate }
];

const DOC_TYPE_INFO = {
  memo: { icon: FileText, label: "Memo", color: "bg-blue-100 text-blue-700" },
  training: { icon: BookOpen, label: "Training", color: "bg-purple-100 text-purple-700" },
  quick_reference: { icon: FileType, label: "Quick Ref", color: "bg-teal-100 text-teal-700" },
  checklist: { icon: ClipboardList, label: "Checklist", color: "bg-green-100 text-green-700" },
  policy: { icon: ScrollText, label: "Policy", color: "bg-orange-100 text-orange-700" },
  patient_education: { icon: Users, label: "Patient Ed", color: "bg-pink-100 text-pink-700" },
  workflow: { icon: GitBranch, label: "Workflow", color: "bg-indigo-100 text-indigo-700" },
  signage: { icon: Signpost, label: "Signage", color: "bg-amber-100 text-amber-700" },
  a3: { icon: LayoutTemplate, label: "A3 Report", color: "bg-cyan-100 text-cyan-700" }
};

export default function Documents() {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeType, setActiveType] = useState("all");
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    fetchDocuments();
    fetchProjects();
    fetchLogo();
  }, []);

  const fetchLogo = async () => {
    try {
      const res = await fetch(`${API_URL}/api/settings/logo`);
      const data = await res.json();
      if (data.exists && data.data) {
        setLogoUrl(`data:${data.content_type};base64,${data.data}`);
      }
    } catch (error) {
      console.error("Failed to fetch logo:", error);
    }
  };

  const fetchDocuments = async () => {
    try {
      const res = await fetch(`${API_URL}/api/documents`);
      const data = await res.json();
      setDocuments(data);
    } catch (error) {
      console.error("Failed to fetch documents:", error);
      toast.error("Failed to load documents");
    }
    setLoading(false);
  };

  const fetchProjects = async () => {
    try {
      const res = await fetch(`${API_URL}/api/projects`);
      const data = await res.json();
      setProjects(data);
    } catch (error) {
      console.error("Failed to fetch projects:", error);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    
    try {
      const res = await fetch(`${API_URL}/api/documents/${docId}`, {
        method: "DELETE"
      });
      if (res.ok) {
        toast.success("Document deleted");
        setDocuments(documents.filter(d => d.id !== docId));
      }
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project ? project.name : "No Project";
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = activeType === "all" || doc.doc_type === activeType;
    return matchesSearch && matchesType;
  });

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          {logoUrl ? (
            <img src={logoUrl} alt="Logo" className="h-12 w-auto max-w-[160px] object-contain" />
          ) : (
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#00B4C5] to-[#6A26B5] flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white" />
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Documents</h1>
            <p className="text-sm text-slate-500 mt-1">
              Memos, training materials, and reference guides
            </p>
          </div>
        </div>
        
        <Button 
          onClick={() => navigate("/documents/new")}
          className="bg-[#6A26B5] hover:bg-[#5a1f9e]"
          data-testid="new-document-btn"
        >
          <FilePlus className="w-4 h-4 mr-2" />
          New Document
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search documents..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="search-documents-input"
        />
      </div>

      {/* Document Type Tabs */}
      <Tabs value={activeType} onValueChange={setActiveType}>
        <TabsList className="flex-wrap h-auto gap-1 bg-transparent p-0">
          {DOC_TYPES.map((type) => {
            const Icon = type.icon;
            const count = type.id === "all" 
              ? documents.length 
              : documents.filter(d => d.doc_type === type.id).length;
            
            return (
              <TabsTrigger 
                key={type.id} 
                value={type.id}
                className="data-[state=active]:bg-[#6A26B5] data-[state=active]:text-white rounded-full px-4"
              >
                <Icon className="w-4 h-4 mr-2" />
                {type.label}
                <Badge variant="secondary" className="ml-2 text-xs">
                  {count}
                </Badge>
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value={activeType} className="mt-6">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="pt-6">
                    <div className="h-6 bg-slate-200 rounded w-3/4 mb-4" />
                    <div className="h-4 bg-slate-100 rounded w-full mb-2" />
                    <div className="h-4 bg-slate-100 rounded w-2/3" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : filteredDocuments.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <FileText className="w-12 h-12 text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-600 mb-2">
                  {searchTerm ? "No documents found" : "No documents yet"}
                </h3>
                <p className="text-sm text-slate-500 mb-4">
                  {searchTerm ? "Try a different search term" : "Create your first document"}
                </p>
                {!searchTerm && (
                  <Button 
                    onClick={() => navigate("/documents/new")}
                    className="bg-[#6A26B5] hover:bg-[#5a1f9e]"
                  >
                    <FilePlus className="w-4 h-4 mr-2" />
                    Create Document
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredDocuments.map((doc) => {
                const typeInfo = DOC_TYPE_INFO[doc.doc_type] || DOC_TYPE_INFO.memo;
                const TypeIcon = typeInfo.icon;
                
                return (
                  <Card 
                    key={doc.id} 
                    className="hover:shadow-md transition-shadow cursor-pointer group"
                    onClick={() => navigate(`/documents/${doc.id}`)}
                    data-testid={`document-card-${doc.id}`}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${typeInfo.color}`}>
                          <TypeIcon className="w-5 h-5" />
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/documents/${doc.id}`);
                            }}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(doc.id);
                              }}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      <CardTitle className="text-lg mt-3 line-clamp-2 group-hover:text-[#6A26B5]">
                        {doc.title}
                      </CardTitle>
                      <CardDescription className="flex items-center gap-2 text-xs">
                        <Badge variant="outline">{typeInfo.label}</Badge>
                        {doc.project_id && (
                          <span className="flex items-center gap-1 text-slate-400">
                            <Folder className="w-3 h-3" />
                            {getProjectName(doc.project_id)}
                          </span>
                        )}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="flex items-center justify-between">
                        <Badge className={doc.status === "published" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}>
                          {doc.status}
                        </Badge>
                        <span className="flex items-center gap-1 text-xs text-slate-500">
                          <Clock className="w-3 h-3" />
                          {formatDate(doc.updated_at)}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
